import { useEffect, useState } from "react";
import { useModalMeta, useSetModal } from "@/shared/atoms/useModal";
import Modal, { ModalProps } from "../Modal/Modal";
import CustomerFinder from "@/Components/CustomerFinder";
import usePosOrderUser from "@/shared/atoms/usePosOrderUser";
import useIsOrderUserValidHook from "@/shared/hooks/useIsOrderUserValidHook";
import useVoipCurrentQuery from "@/shared/queries/voip/useVoipCurrentQuery";
import useCustomerFinderHook from "@/shared/hooks/useCustomerFinderHook";
import FormOrderUserAddress from "../OrderUser/FormOrderUserAddress";
import FormOrderUserDetails from "../OrderUser/FormOrderUserDetails";
import FormOrderUserActions from "../OrderUser/FormOrderUserActions";
import { defaultOrderUserFormValues } from "../OrderUser/constant";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

const OrderUserModal = ({ ...props }: ModalProps) => {
  const setModal = useSetModal();
  const [{ deliveryMethodId }] = useModalMeta();
  const [search, setSearch] = useState("");
  const { queryKey: voipQueryKey, ...voipQuery } = useVoipCurrentQuery();
  const [orderUser, setOrderUser] = usePosOrderUser();
  const { isOrderUserValid } = useIsOrderUserValidHook({
    selectedDelivery: deliveryMethodId,
    orderUser,
  });
  const methods = useForm({ mode: "all" });
  const { handleSubmit, reset, getValues, trigger } = methods;
  const { fetchCustomers, setCustomers, customers, isCustomerPending } =
    useCustomerFinderHook(getValues(), search);
  const queryClient = useQueryClient();

  useEffect(() => {
    isOrderUserValid === undefined && trigger();
  }, [isOrderUserValid, trigger]);

  useEffect(() => {
    if (voipQuery.data?.users?.length === 1) {
      setOrderUser({
        ...defaultOrderUserFormValues,
        ...voipQuery.data.users[0],
      });
    }
  }, [voipQuery.data?.users, setOrderUser]);

  const onSubmit = (values) => {
    setOrderUser({ ...values.order_user, item_id: orderUser?.item_id });
    setModal("", {});
  };

  const handleOnSelectCustomer = (data) => {
    if (data) {
      setOrderUser({ ...defaultOrderUserFormValues, ...data });
      reset();
    }
    if (voipQuery.data?.users?.length > 1) {
      queryClient.removeQueries({ queryKey: voipQueryKey });
    }
    setCustomers([]);
  };

  const customerList =
    customers?.length > 0
      ? customers
      : voipQuery.data?.users?.length > 1
        ? voipQuery.data.users
        : [];

  return (
    <Modal width="80rem" minHeight="35rem" {...props}>
      <CustomerFinder
        customers={customerList}
        onSelectCustomer={handleOnSelectCustomer}
        search={search}
        setSearch={setSearch}
        onClickSearch={() => fetchCustomers(true)}
      />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormOrderUserAddress />
          <FormOrderUserDetails />
          <FormOrderUserActions
            isCustomerPending={isCustomerPending}
            onClickSearch={() => fetchCustomers(false)}
          />
        </form>
      </FormProvider>
    </Modal>
  );
};

export default OrderUserModal;
